import { Select } from '@aigc/antd';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import { tryURL } from '@aigc/toolkit';
import {
  InboxOutlined,
  PlaySquareOutlined,
  VideoCameraOutlined,
  VideoCameraTwoTone,
} from '@ant-design/icons';
import { useLens } from '@hookform/lenses';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  App,
  Button,
  Empty,
  Form,
  Popconfirm,
  Upload,
  type UploadFile,
} from 'antd';
import { RESET } from 'jotai/utils';
import { useEffect } from 'react';
import { useForm, Watch } from 'react-hook-form';
import { v7 as uuid } from 'uuid';
import { array, mixed, number, object, string, tuple } from 'yup';
import { FormControl } from '../../../components/form-control';
import { UploadedImageListModal } from '../../../components/uploaded-image-list-modal';
import { VirtualAvatarSelect } from '../../../features/assets/components/virtual-avatar-select';
import { Permission } from '../../../features/common/permision';
import {
  createBeforeUpload,
  createUploadValidator,
} from '../../../features/file';
import { usePreviewFn } from '../../../features/file/hooks';
import type {
  FileDto,
  UploadFileOutputDto,
} from '../../../features/file/model';
import {
  toFileInputDtoList,
  toFileListFormValues,
} from '../../../features/file/utils';
import {
  DEFAULT_REFERENCE_IMAGE_MODEL,
  REFERENCE_IMAGE_MODEL_MAP,
} from '../../../features/reference-image/constant';
import { TaskType } from '../../../features/task/model';
import { PromptControl } from '../../../features/video-model/components/prompt-control';
import {
  GenerationType,
  isSupportedAssets,
  isVideoEditModelSupported,
  videoModelSpecs,
  type VideoModelCode,
} from '../../../features/video-model/model-spec';
import { getEditVideoMutationOptions } from '../../../features/video/api';
import { VideoGenerationTaskTable } from '../../../features/video/components/task-table';
import { VideoRangeSelector } from '../../../features/video/components/video-range-selector';
import {
  useReadVideoEditPageData,
  useSetVideoEditPageData,
} from '../../../features/video/store';

const videoEditSchema = object({
  model: string<VideoModelCode>().required('请选择生成平台'),
  prompt: string().required('请输入提示词'),
  time_range: tuple([number().required(), number().required()])
    .when(['model'], (values, schema) => {
      const [model] = values as [VideoModelCode];
      const spec = videoModelSpecs.get(model);
      if (!spec) {
        return schema;
      }

      if (Array.isArray(spec.duration)) {
        return schema;
      }

      const modelName = videoModelSpecs.get(model)?.name ?? model;

      const { min, max } = spec.duration;
      let newSchema = schema;

      if (min) {
        newSchema = newSchema.test({
          name: 'min',
          message: `${modelName} 至少重生成 ${min} 秒的视频`,
          test: (value) => {
            if (!value) {
              return true;
            }

            const [startTime, endTime] = value;

            return endTime - startTime >= min;
          },
        });
      }

      if (max) {
        newSchema = newSchema.test({
          name: 'max',
          message: `${modelName} 至多重生成 ${min} 秒的视频`,
          test: (value) => {
            if (!value) {
              return true;
            }

            const [startTime, endTime] = value;
            return endTime - startTime <= max;
          },
        });
      }

      return newSchema;
    })
    .nullable()
    .required(),
  avatar_ids: array().of(string().required()).nullable(),
  image_list: array()
    .of(mixed<UploadFile<UploadFileOutputDto>>().required())
    .when(['model'], (values, schema) => {
      const [model] = values as [VideoModelCode];
      const spec = videoModelSpecs.get(model);
      if (!spec) {
        return schema;
      }

      const modelName = videoModelSpecs.get(model)?.name ?? model;

      if (!spec.image_number?.reference) {
        return schema;
      }

      const { max } = spec.image_number.reference;
      let newSchema = schema;

      if (max) {
        newSchema = newSchema.test({
          name: 'max',
          message: `${modelName} 最多支持 ${max} 张参考图`,
          test: (value) => {
            return number()
              .max(max)
              .isValidSync(value?.length ?? 0);
          },
        });
      }

      return newSchema;
    })
    .test((values, ctx) => {
      if (!values?.length) {
        return true;
      }

      if (values.some((item) => item.status === 'uploading')) {
        return ctx.createError({
          message: '文件正在上传中，请稍等',
        });
      }

      if (values.every((item) => item.status !== 'done')) {
        return ctx.createError({
          message: '文件均上传失败，请重新上传',
        });
      }

      return true;
    })
    .nullable(),
  video_list: array()
    .of(mixed<UploadFile<UploadFileOutputDto>>().required())
    .min(1, '请上传要编辑的视频')
    .test((values, ctx) => {
      if (!values?.length) {
        return true;
      }

      if (values.some((item) => item.status === 'uploading')) {
        return ctx.createError({
          message: '文件正在上传中，请稍等',
        });
      }

      if (values.every((item) => item.status !== 'done')) {
        return ctx.createError({
          message: '文件均上传失败，请重新上传',
        });
      }

      return true;
    })
    .nullable()
    .required('请上传待编辑视频'),
});

const VideoEdit = () => {
  const { notification } = App.useApp();
  const onPreview = usePreviewFn();
  const [openOverlay] = useOverlay<FileDto>();
  const readPageData = useReadVideoEditPageData();
  const setPageData = useSetVideoEditPageData();
  const pageData = readPageData();

  const form = useForm({
    defaultValues: {
      model: DEFAULT_REFERENCE_IMAGE_MODEL,
      prompt: '',
      video_list: [],
      avatar_ids: [],
      image_list: [],
      time_range: null!,
    },
    resolver: yupResolver(videoEditSchema),
  });

  const { control, handleSubmit, subscribe, setValues, getValues, setValue } =
    form;
  const lens = useLens({ control });

  const editVideo = useMutation(getEditVideoMutationOptions());

  const modelOptions = Array.from(
    REFERENCE_IMAGE_MODEL_MAP.entries()
      .filter(([_, spec]) => !spec.deprecated)
      .filter(([code]) => isVideoEditModelSupported(code)),
  ).map(([code, spec]) => ({
    label: spec.name,
    value: code,
  }));

  useEffect(() => {
    setValues({
      model: pageData.model ?? DEFAULT_REFERENCE_IMAGE_MODEL,
      image_list: toFileListFormValues(pageData.images ?? []),
      avatar_ids: pageData.avatar_ids ?? [],
      prompt: pageData.prompt ?? '',
      video_list: toFileListFormValues(
        pageData.filename ? [pageData.filename] : [],
      ),
      time_range: null!,
    });
  }, []);

  // 持久化表单状态（仅持久化界面控件对应的字段）
  useEffect(() => {
    return subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        setPageData((prev) => ({
          ...prev,
          model: values.model,
          images: toFileInputDtoList(values.image_list),
          filename: values.video_list.at(0)?.response?.file_url,
          prompt: values.prompt,
          avatar_ids: values.avatar_ids ?? [],
        }));
      },
    });
  }, [subscribe, setPageData]);

  const handleSubmitEdit = handleSubmit(async (values) => {
    const [startTime, endTime] = values.time_range;

    const images = toFileInputDtoList(values.image_list);

    await editVideo.mutateAsync({
      model: values.model,
      start_time: startTime,
      end_time: endTime,
      prompt: values.prompt,
      images,
      avatar_ids: values.avatar_ids?.length ? values.avatar_ids : undefined,
      filename: values.video_list.at(0)!.response!.file_url,
    });

    notification.success({ title: '成功', description: '视频编辑任务已提交' });
  });

  const acceptVideo = useAccept(['video/*']);
  useAutoPaste('#videoUploader', {
    filter: acceptVideo.filter,
  });

  const acceptImage = useAccept(['.jpg', '.jpeg', '.png', '.webp']);
  useAutoPaste('#imageUploader', {
    filter: acceptImage.filter,
  });

  return (
    <div className="flex w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:h-full sm:flex-row sm:divide-x sm:divide-y-0">
      {/* 左侧操作面板 */}
      <div className="card animate-fade-in sm:overflow-auto xl:w-[480px]">
        {/* 参考图片/视频 */}
        <Form layout="vertical">
          <FormControl
            control={control}
            name="video_list"
            label="原视频"
            exact
            required
            render={({ field }) => (
              <Upload.Dragger
                id="videoUploader"
                ref={field.ref}
                name={field.name}
                disabled={field.disabled}
                classNames={{ list: 'mt-2' }}
                fileList={field.value}
                accept={acceptVideo}
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({
                    accept: acceptVideo.format,
                  }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                onPreview={onPreview}
                onChange={(info) => {
                  const { status } = info.file;

                  if (status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `图片 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  if (info.file.status === 'removed') {
                    field.onChange([]);
                  } else {
                    field.onChange([info.file]);
                  }
                }}
              >
                <p className="ant-upload-drag-icon">
                  <PlaySquareOutlined />
                </p>
                <p className="ant-upload-text">
                  点击、拖放或<span className="text-blue-500">粘贴</span>
                  上传待编辑视频
                </p>
              </Upload.Dragger>
            )}
          ></FormControl>
          <FormControl
            control={control}
            name="image_list"
            label="参考图片"
            exact
            render={({ field }) => {
              return (
                <div>
                  <Button
                    classNames={{ root: 'mb-2' }}
                    variant="filled"
                    color="default"
                    block
                    onClick={async () => {
                      const overlayRef = openOverlay(
                        <UploadedImageListModal></UploadedImageListModal>,
                      );

                      const file = await overlayRef.afterClosed;

                      if (!file) {
                        return;
                      }

                      const url = tryURL(file.oss_object_key, file.oss_host);

                      if (!url) {
                        return;
                      }

                      setValue('image_list', [
                        ...(getValues('image_list') ?? []),
                        {
                          uid: uuid(),
                          name: file.filename,
                          url: url.toString(),
                          thumbUrl: url.toString(),
                          status: 'done',
                          response: {
                            id: file.id,
                            filename: file.filename,
                            mime: file.mime,
                            file_url: url.toString(),
                            created_at: file.created_at,
                          },
                        },
                      ]);
                    }}
                  >
                    从图片库选择
                  </Button>
                  <Upload.Dragger
                    id="imageUploader"
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    classNames={{ list: 'mt-2' }}
                    listType="picture-card"
                    multiple
                    fileList={field.value ?? []}
                    accept={acceptImage}
                    onPreview={onPreview}
                    beforeUpload={createBeforeUpload({
                      validator: createUploadValidator({
                        accept: acceptImage.format,
                      }),
                      onError: ({ title, message }) => {
                        notification.error({ title, description: message });
                      },
                    })}
                    iconRender={(file) => {
                      if (file.type?.startsWith('video/')) {
                        let thumbnail = '';

                        if (file.response?.file_url) {
                          const url = tryURL(file.response.file_url);

                          if (url) {
                            url.searchParams.set(
                              'x-oss-process',
                              'video/snapshot,t_1000,f_jpg,w_320,h_320',
                            );
                            thumbnail = url.toString();
                          }
                        }

                        if (thumbnail) {
                          return (
                            <div
                              className="flex h-full w-full items-center justify-center"
                              style={{
                                backgroundImage: `url("${thumbnail}")`,
                                backgroundSize: 'cover',
                              }}
                            >
                              <VideoCameraOutlined className="opacity-9 !text-white !text-opacity-80" />
                            </div>
                          );
                        }

                        return (
                          <div className="flex h-full w-full items-center justify-center">
                            <VideoCameraTwoTone />
                          </div>
                        );
                      }

                      return (
                        <img
                          src={file.thumbUrl || file.url}
                          alt={file.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      );
                    }}
                    showUploadList={{
                      showRemoveIcon: true,
                    }}
                    onChange={(info) => {
                      const { status } = info.file;

                      if (status === 'error') {
                        notification.error({
                          title: '错误',
                          description: `图片 ${info.file.name} 上传失败，请重试`,
                        });
                      }

                      field.onChange(
                        info.fileList.map((item) => {
                          if (item.response) {
                            item.url = item.response.file_url;
                            item.thumbUrl = item.response.file_url;
                          }

                          return item;
                        }),
                      );
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      点击、拖放或<span className="text-blue-500">粘贴</span>
                      上传产品参考图片
                    </p>
                  </Upload.Dragger>
                </div>
              );
            }}
          ></FormControl>
          {/* 虚拟人像 — 仅 Seedance */}
          <Watch
            control={control}
            name="model"
            render={(value) =>
              videoModelSpecs.get(value)?.reference_element && (
                <FormControl
                  control={control}
                  name="avatar_ids"
                  label="虚拟人像"
                  render={({ field }) => (
                    <VirtualAvatarSelect
                      mode="multiple"
                      {...field}
                    ></VirtualAvatarSelect>
                  )}
                ></FormControl>
              )
            }
          ></Watch>
          <FormControl
            control={control}
            name="model"
            label="生成平台"
            required
            render={({ field }) => (
              <Select
                {...field}
                allowClear={false}
                options={modelOptions}
              ></Select>
            )}
          ></FormControl>
          {/* 提示词 */}
          <Watch
            control={control}
            name={['image_list', 'avatar_ids', 'model']}
            render={([image_list, avatar_ids, model]) => {
              return (
                <PromptControl
                  generationType={GenerationType.Reference}
                  images={image_list ?? []}
                  asset_ids={
                    isSupportedAssets(model) ? (avatar_ids ?? void 0) : void 0
                  }
                  modelLens={lens.focus('model')}
                  lens={lens.focus('prompt').cast()}
                  autoSize={{ minRows: 6 }}
                ></PromptControl>
              );
            }}
          ></Watch>
        </Form>

        {/* 操作栏 */}
        <div className="sticky bottom-4 flex space-x-2">
          <Popconfirm
            title="确定要重置吗？"
            onConfirm={() => {
              form.reset();
              setPageData(RESET);
            }}
          >
            <Button size="large" variant="solid" color="primary">
              重置
            </Button>
          </Popconfirm>
          <Popconfirm title="确定要生成视频吗？" onConfirm={handleSubmitEdit}>
            <Button
              variant="solid"
              color="primary"
              size="large"
              loading={editVideo.isPending}
              block
            >
              {editVideo.isPending ? '生成中...' : '✨ 生成视频'}
            </Button>
          </Popconfirm>
        </div>

        {/* 任务列表 */}
        <VideoGenerationTaskTable
          taskType={[TaskType.视频编辑]}
          onEditVideo={(filename) => {
            setValues({
              video_list: toFileListFormValues([filename]),
              time_range: null!,
            });
          }}
        ></VideoGenerationTaskTable>
      </div>

      {/* 右侧内容区 */}
      <div className="card flex-auto animate-slide-up space-y-6 sm:min-h-40 sm:overflow-auto xl:flex-1">
        {/* 视频替换范围 */}
        <Watch
          control={control}
          name="video_list"
          compute={(value) => {
            const file = value.at(0);

            if (!file) {
              return null;
            }

            if (file.status !== 'done') {
              return null;
            }

            return file;
          }}
          render={(file) => {
            if (!file) {
              return <Empty description="请上传你要编辑的视频文件"></Empty>;
            }

            return (
              <FormControl
                control={control}
                name="time_range"
                render={({ field }) => (
                  <VideoRangeSelector
                    {...field}
                    videoSrc={file.response?.file_url ?? ''}
                  ></VideoRangeSelector>
                )}
              ></FormControl>
            );
          }}
        ></Watch>
        <Alert
          showIcon
          title="使用提示"
          description={
            <div>
              <ol>
                <li>
                  <span className="font-bold">操作步骤</span>
                </li>
                <li>1. 上传要编辑的原视频</li>
                <li>
                  2. 拖拽时间轴上的
                  <span className="text-yellow-500">黄色手柄</span>
                  控制并预览要编辑的视频内容区间窗口
                </li>
                <li>
                  3.
                  输入编辑提示词，例如：将模特身上的衣服颜色改成红色；去除画面左上角的水印
                </li>
                <li>4. 点击生成视频</li>
              </ol>
              <ol className="mt-4">
                <li>
                  <span className="font-bold">操作技巧</span>
                </li>
                <li>1. 点击时间轴的区间窗口可以播放预览待编辑的片段</li>
                <li>
                  2. 鼠标指针放置在区间窗口内后按住鼠标可以整体拖拽区间窗口
                </li>
                <li>3. 拖拽区间窗口内的白色把手可以逐帧预览区间内的画面</li>
              </ol>
            </div>
          }
        ></Alert>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_creator-center/video/edit')({
  staticData: { permissions: [Permission.视频编辑] },
  component: VideoEdit,
});
